import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, PageHeader, FormShell, LightboxBackdrop, LightboxImage,
} from '../../styles/CustomerSpecsIndexStyles';
import authAxios from '../../utils/authAxios';
import { useAuth } from '../../context/AuthContext';
import { errorToast, savedToast, warnToast } from '../../utils/toastUtils';
import { canonicalCustomerId, zeroBagStyleNames, tiedBagStyles } from '../../utils/bagLabelRules';
import { getOrderedSizesForCommodity } from '../../utils/sizeOptions';
import {
  NO_PLU_LABEL,
  normalizeUpcOptionPayload,
  upcSelectionsToPayload,
} from '../../utils/customerSpecUpc';
import { isValidManual } from '../../components/ManualUpcField';
import { digitsOnly } from '../../utils/barcodeUtils';
import { isSpecSelectablePidStatus } from '../../utils/pidStatus';
import { buildTiered, canonTieredEntries, reTier } from '../../utils/tieredGrades';
import { toCsv } from '../../utils/csvList';
import StatusPicker from '../../components/StatusPicker';
import { DEFAULT_SPEC_STATUS } from '../../utils/specStatus';
import SpecFormFields from '../../components/SpecFormFields';

// Initial form state (matches agreed data model)
const initialForm = {
  CustomerID: '',
  Status: DEFAULT_SPEC_STATUS,
  Destinations: [],
  DomesticOrImport: '',
  Universal: false,
  CommodityID: '',
  Varieties: [],
  BWCType: '',
  BWCValue: '',
  BWCDisplay: '',
  RecommendedSizingList: [], // selected sizes, stored in commodity (Size-Options) order
  SpecSizeRangeList: [], // selected sizes, stored in commodity (Size-Options) order
  SubbingOptions: [],
  ApprovalNeeded: '',
  SizingNotes: '',
  TechnicalNotes: '',
  Grades: [],
  StyleIDs: [],
  LabelIDs: [],
  MethodIDs: [],
  upcSelectionKeys: [],
  upcMode: 'catalog', // 'catalog' (pick from PID) | 'custom' (typed bypass code)
  manualUpc: '',      // single typed UPC-A/EAN-13 when upcMode === 'custom'
  PackagingNotes: '',
  CustomerItemNumber: '',
  QualityNotes: '',
  GTIN: '',
  TemperatureRecorder: '',
  PalletTypes: [],
  TiHiIDs: [],
  ShippingNotes: '',
  CaseTagID: '',
  BagLabelNotes: '',
};

const CustomerSpecsFormPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState(initialForm);

  // Options
  const [customers, setCustomers] = useState([]);
  const [customerRows, setCustomerRows] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [varieties, setVarieties] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [gradesAll, setGradesAll] = useState([]);
  const [styles, setStyles] = useState([]);
  const [labels, setLabels] = useState([]);
  const [bagLabelRows, setBagLabelRows] = useState([]); // dbo.BagLabels — per-style bag-label rules
  const [pluMap, setPluMap] = useState({}); // LabelID -> Set(PLU UniqueID)
  const [pluImagesByLabelId, setPluImagesByLabelId] = useState({}); // LabelID -> string[] image urls
  const [pluImagesCache, setPluImagesCache] = useState({}); // PLU UniqueID -> string[] image urls
  const [methods, setMethods] = useState([]); // all dbo.Methods — for display resolution
  const [methodOptions, setMethodOptions] = useState([]); // PID-backed Active/Incoming — for the picker
  const [upcOptions, setUpcOptions] = useState([]);
  const [upcRefs, setUpcRefs] = useState([]);
  const [tihi, setTihi] = useState([]);
  const [caseTags, setCaseTags] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialSnapshotRef = useRef(null);

  // Track dirty state relative to initial snapshot
  useEffect(() => {
    if (!initialSnapshotRef.current) initialSnapshotRef.current = formData;
    const snap = initialSnapshotRef.current;
    // Canonicalize tiered entries (collapse ALL/None to one sentinel, ignore grade
    // order) so grade auto-widen on a grade-set change never phantom-dirties the form.
    const canonFor = (fd) => ({
      ...fd,
      StyleIDs: canonTieredEntries(fd.StyleIDs, fd.Grades),
      LabelIDs: canonTieredEntries(fd.LabelIDs, fd.Grades),
      MethodIDs: canonTieredEntries(fd.MethodIDs, fd.Grades),
    });
    setIsDirty(JSON.stringify(canonFor(snap)) !== JSON.stringify(canonFor(formData)));
  }, [formData]);

  // Unsaved changes guards (beforeunload and basic client-side nav intercept)
  useEffect(() => {
    const beforeUnload = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', beforeUnload);
    const onClick = (e) => {
      if (!isDirty) return;
      const a = e.target?.closest && e.target.closest('a[href]');
      if (a && a.target !== '_blank' && a.origin === window.location.origin) {
        const ok = window.confirm('You have unsaved changes. Leave this page?');
        if (!ok) e.preventDefault();
      }
    };
    const onPop = () => {
      if (!isDirty) return;
      const ok = window.confirm('You have unsaved changes. Leave this page?');
      if (!ok) {
        window.history.pushState(null, '', document.location.href);
      }
    };
    document.addEventListener('click', onClick);
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('click', onClick);
      window.removeEventListener('popstate', onPop);
    };
  }, [isDirty]);

  // Derived: destinations filtered by customer
  const destinationOptions = useMemo(() => {
    if (!formData.CustomerID) return [];
    const chosenName = customers.find(c => String(c.UniqueID) === String(formData.CustomerID))?.Customer;
    if (!chosenName) return [];
    const rows = customerRows.filter(r => (r.Customer || '').trim() === (chosenName || '').trim());
    const set = new Set(rows.map(r => String(r.Destination || '').trim()).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [formData.CustomerID, customers, customerRows]);

  const sizeOptionsForCommodity = useMemo(() => {
    return getOrderedSizesForCommodity(sizes, formData.CommodityID, formData.DomesticOrImport);
  }, [formData.CommodityID, formData.DomesticOrImport, sizes]);

  const varietyOptionsForCommodity = useMemo(() => {
    if (!formData.CommodityID) return [];
    const cid = Number(formData.CommodityID);
    return varieties
      .filter(v => Number(v.CommodityID) === cid)
      .map(v => v.Variety)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [formData.CommodityID, varieties]);

  const gradeOptionsForCommodity = useMemo(() => {
    if (!formData.CommodityID) return [];
    const cid = Number(formData.CommodityID);
    return (gradesAll || [])
      .filter(g => Number(g.Commodity) === cid)
      .map(g => g.GradeName)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [formData.CommodityID, gradesAll]);

  // Load options
  useEffect(() => {
    (async () => {
      try {
        const [custDD, custAll, commAll, varRes, sizeDD, gradeRes, packRes, bags, plus, methRes, methOptRes, upcOptRes, tihiRes, tagRes, bagLabelRes] = await Promise.all([
          authAxios.get('/api/customers/dropdown'),
          authAxios.get('/api/customers'),
          authAxios.get('/api/commodities'),
          authAxios.get('/api/varieties'),
          authAxios.get('/api/sizeoptions/dropdown'),
          authAxios.get('/api/grades'),
          authAxios.get('/api/packtypes'),
          authAxios.get('/api/pidbags'),
          authAxios.get('/api/pidplu'),
          authAxios.get('/api/methods'),
          authAxios.get('/api/customer-specs/method-options'),
          authAxios.get('/api/customer-specs/upc-options'),
          authAxios.get('/api/tihi'),
          authAxios.get('/api/casetags'),
          authAxios.get('/api/baglabels'),
        ]);
        setCustomers(custDD.data || []);
        setCustomerRows(custAll.data || []);
        setCommodities(commAll.data || []);
        setVarieties(varRes.data || []);
        setSizes(sizeDD.data || []);
        setGradesAll(gradeRes.data || []);
        setStyles(packRes.data || []);
        // Build union label IDs
        // Curate the label add-pool to Active + Incoming PID labels (a spec may reference a
        // live or coming-soon label; Pending/Inactive are not offered). An already-selected
        // non-selectable label still renders via TieredGradeList displayFor(id)=id, so no
        // separate keep-selected guard is needed here.
        const isSelectable = (s) => isSpecSelectablePidStatus(s);
        const bagLabels = new Set((bags.data || []).filter(b => isSelectable(b.Status)).map(b => String(b.LABEL_ID)).filter(Boolean));
        const pluLabels = new Set((plus.data || []).filter(p => isSelectable(p.Status)).map(p => String(p.LabelID)).filter(Boolean));
        const allLabels = Array.from(new Set([...bagLabels, ...pluLabels])).sort((a, b) => a.localeCompare(b));
        setLabels(allLabels);
        // PLU map for display
        const map = {};
        (plus.data || []).forEach(p => {
          const k = String(p.LabelID || '');
          if (!k) return;
          if (!map[k]) map[k] = new Set();
          map[k].add(p.UniqueID);
        });
        const out = {};
        Object.keys(map).forEach(k => out[k] = Array.from(map[k]).sort((a, b) => a - b));
        setPluMap(out);
        setMethods(methRes.data || []);
        setMethodOptions(methOptRes.data || []);
        const normalizedUpcs = normalizeUpcOptionPayload(upcOptRes.data || {});
        setUpcOptions(normalizedUpcs.options);
        setUpcRefs(normalizedUpcs.refs);
        setTihi(tihiRes.data || []);
        setCaseTags(tagRes.data || []);
        setBagLabelRows(bagLabelRes.data || []);
      } catch (e) {
        console.error('[CustomerSpecs] Load options failed', e);
      }
    })();
  }, []);

  // When LabelIDs change, fetch PLU images for any PIDPLU label (supports multiple)
  useEffect(() => {
    (async () => {
      const labelVals = (formData.LabelIDs || []).map(e => (e && typeof e === 'object') ? e.value : e);
      const labels = labelVals.filter(l => pluMap[l]);
      if (!labels.length) return;
      const newLabelToImages = { ...pluImagesByLabelId };
      const newCache = { ...pluImagesCache };
      let changed = false;

      for (const lbl of labels) {
        const ids = pluMap[lbl] || [];
        const allImages = [];
        for (const id of ids) {
          const key = Number(id);
          let imgs = newCache[key];
          if (!imgs) {
            try {
              const imgRes = await authAxios.get(`/api/pidplu/images?pluId=${key}`);
              imgs = Array.isArray(imgRes.data) ? imgRes.data : [];
            } catch {
              imgs = [];
            }
            newCache[key] = imgs;
          }
          imgs.forEach(u => allImages.push(u));
        }
        const unique = Array.from(new Set(allImages));
        if (JSON.stringify(unique) !== JSON.stringify(newLabelToImages[lbl] || [])) {
          newLabelToImages[lbl] = unique;
          changed = true;
        }
      }
      if (changed) setPluImagesByLabelId(newLabelToImages);
      if (Object.keys(newCache).length !== Object.keys(pluImagesCache).length) setPluImagesCache(newCache);
    })();
  }, [formData.LabelIDs, pluMap, pluImagesByLabelId, pluImagesCache]);

  const computeBwcDisplay = (type, value) => {
    if (type === 'BULK') return 'BULK';
    if (!value) return '';
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    if (type === 'Weight') {
      const kg = num * 0.45359237;
      const fmt = (n) => (n % 1 === 0 ? String(n) : n.toFixed(2));
      return `${fmt(num)}lb (${fmt(Number(kg.toFixed(2)))}kg)`;
    }
    if (type === 'Count') return `${num}CT`;
    return '';
  };

  useEffect(() => {
    setFormData(prev => ({ ...prev, BWCDisplay: computeBwcDisplay(prev.BWCType, prev.BWCValue) }));
  }, [formData.BWCType, formData.BWCValue]);

  // Prune dependent selections when commodity changes
  const lastCommodityRef = useRef(null);
  useEffect(() => {
    const allowedVarieties = new Set(varietyOptionsForCommodity);
    const allowedGrades = new Set(gradeOptionsForCommodity);
    const allowedSizes = new Set(sizeOptionsForCommodity);
    const commodityChanged = String(lastCommodityRef.current || '') !== String(formData.CommodityID || '');
    lastCommodityRef.current = formData.CommodityID;

    setFormData(prev => ({
      ...prev,
      Varieties: (prev.Varieties || []).filter(v => allowedVarieties.has(v)),
      Grades: (prev.Grades || []).filter(g => allowedGrades.has(g)),
      SubbingOptions: (prev.SubbingOptions || []).filter(s => allowedSizes.has(s)),
      // New specs and commodity changes start empty; otherwise keep only sizes still
      // valid for the commodity. Selections persist in commodity (Size-Options) order.
      RecommendedSizingList: commodityChanged
        ? []
        : (prev.RecommendedSizingList || []).filter(s => allowedSizes.has(s)),
      SpecSizeRangeList: commodityChanged
        ? []
        : (prev.SpecSizeRangeList || []).filter(s => allowedSizes.has(s)),
    }));
  }, [formData.CommodityID, varietyOptionsForCommodity, sizeOptionsForCommodity, gradeOptionsForCommodity]);

  // Re-tier entry grades whenever the spec's grade set changes (commodity prune mutates
  // Grades, or direct Grades-chip edits): auto-widen entries that were ALL, intersect
  // subsets against the new set, never None at G>=1. Keyed on Grades so it runs once
  // Grades settles; prevGradesRef judges "was ALL" against the OLD set.
  const prevGradesRef = useRef(null);
  useEffect(() => {
    const newGrades = formData.Grades || [];
    const prev = prevGradesRef.current;
    prevGradesRef.current = newGrades;
    if (prev === null) return; // prime on first run; don't widen
    setFormData(p => {
      const nextStyle = reTier(p.StyleIDs, prev, newGrades);
      const nextLabel = reTier(p.LabelIDs, prev, newGrades);
      const nextMethod = reTier(p.MethodIDs, prev, newGrades);
      if (JSON.stringify(nextStyle) === JSON.stringify(p.StyleIDs)
        && JSON.stringify(nextLabel) === JSON.stringify(p.LabelIDs)
        && JSON.stringify(nextMethod) === JSON.stringify(p.MethodIDs)) return p;
      return { ...p, StyleIDs: nextStyle, LabelIDs: nextLabel, MethodIDs: nextMethod };
    });
  }, [formData.Grades]);

  // Derived PLUs from selected LabelIDs
  const derivedPLUIds = useMemo(() => {
    const set = new Set();
    (formData.LabelIDs || [])
      .map(e => (e && typeof e === 'object') ? e.value : e)
      .filter((lbl) => lbl && lbl !== NO_PLU_LABEL)
      .forEach(lbl => (pluMap[lbl] || []).forEach(id => set.add(id)));
    return Array.from(set).sort((a, b) => a - b);
  }, [formData.LabelIDs, pluMap]);

  const mergedUpcOptions = upcOptions;
  const upcOptionByValue = useMemo(
    () => new Map([...(upcRefs || []), ...(mergedUpcOptions || [])].map((o) => [o.value, o])),
    [upcRefs, mergedUpcOptions]
  );
  const tihiById = useMemo(() => {
    const map = {};
    (tihi || []).forEach(t => { map[Number(t.UniqueID)] = t.TiHi; });
    return map;
  }, [tihi]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Client-side required validation
    const missing = {};
    if (!formData.CustomerID) missing.CustomerID = true;
    if (!formData.DomesticOrImport) missing.DomesticOrImport = true;
    if (!formData.CommodityID) missing.CommodityID = true;
    if (Object.keys(missing).length) {
      setFieldErrors(missing);
      errorToast('Please complete all required fields.');
      return;
    }
    // A custom UPC, if entered, must be a valid UPC-A/EAN-13 (length is not enough).
    if (formData.upcMode === 'custom' && digitsOnly(formData.manualUpc) && !isValidManual(formData.manualUpc)) {
      errorToast('Enter a valid 12-digit UPC-A or 13-digit EAN-13, or switch back to catalog.');
      return;
    }
    {
      const bagCustId = canonicalCustomerId(customers, { CustomerID: formData.CustomerID });
      const bagCommodityId = Number(formData.CommodityID) > 0 ? Number(formData.CommodityID) : null;
      // Bag-tie block: a Bag style on Default whose rules tie resolves to no label and
      // the ambiguity is resolvable here, so the save is refused until one is picked.
      const ties = tiedBagStyles(formData.StyleIDs, styles, bagLabelRows, bagCustId, bagCommodityId);
      if (ties.length) {
        setFieldErrors({ BagLabelTieStyleIds: ties.map((t) => t.id) });
        errorToast(`Select a bag label for tied style(s): ${ties.map((t) => t.name).join(', ')}`);
        return;
      }
      setFieldErrors({});
      // Bag-zero warning (warn, don't block): flag Bag-type styles resolving to no label.
      const zeroBagStyles = zeroBagStyleNames(formData.StyleIDs, styles, bagLabelRows, bagCustId, bagCommodityId);
      if (zeroBagStyles.length) warnToast(`No bag label resolved for Bag style(s): ${zeroBagStyles.join(', ')}`);
    }
    setIsSubmitting(true);
    try {
      const recommendedList = formData.RecommendedSizingList || [];
      const specRangeList = formData.SpecSizeRangeList || [];
      const recommendedCsv = (() => { const s = toCsv(recommendedList); return s ? s : null; })();
      const specRangeCsv = (() => { const s = toCsv(specRangeList); return s ? s : null; })();
      const subbingCsv = (() => { const s = toCsv(formData.SubbingOptions || []); return s ? s : null; })();
      const payload = {
        CustomerID: Number(formData.CustomerID),
        Status: formData.Status || 'Draft',
        DestinationsCsv: (() => { const s = toCsv(formData.Destinations || []); return s ? s : null; })(),
        DomesticOrImport: formData.DomesticOrImport || '',
        Universal: !!formData.Universal,
        CommodityID: Number(formData.CommodityID),
        VarietiesCsv: (formData.Varieties && formData.Varieties.length) ? formData.Varieties.join(',') : null,
        BWCType: formData.BWCType ? formData.BWCType : null,
        BWCDisplay: formData.BWCDisplay ? formData.BWCDisplay : null,
        // Transitional compatibility: keep legacy SizesCsv in sync with RecommendedSizingCsv
        SizesCsv: recommendedCsv,
        RecommendedSizingCsv: recommendedCsv,
        SpecSizeRangeCsv: specRangeCsv,
        SubbingOptionsCsv: subbingCsv,
        ApprovalNeeded: formData.ApprovalNeeded ? String(formData.ApprovalNeeded) : null,
        SizingNotes: formData.SizingNotes ? String(formData.SizingNotes) : null,
        TechnicalNotes: formData.TechnicalNotes ? formData.TechnicalNotes : null,
        GradesCsv: (() => { const s = (formData.Grades || []).join(','); return s ? s : null; })(),
        PalletTypesCsv: (() => { const s = (formData.PalletTypes || []).join(','); return s ? s : null; })(),
        TiHiIDsCsv: (() => { const s = (formData.TiHiIDs || []).join(','); return s ? s : null; })(),
        PackagingNotes: formData.PackagingNotes ? formData.PackagingNotes : null,
        BagLabelNotes: formData.BagLabelNotes ? formData.BagLabelNotes : null,
        CustomerItemNumber: formData.CustomerItemNumber ? formData.CustomerItemNumber : null,
        QualityNotes: formData.QualityNotes ? formData.QualityNotes : null,
        GTIN: formData.GTIN ? formData.GTIN : null,
        TemperatureRecorder: formData.TemperatureRecorder ? formData.TemperatureRecorder : null,
        ShippingNotes: formData.ShippingNotes ? formData.ShippingNotes : null,
        CaseTagID: formData.CaseTagID ? Number(formData.CaseTagID) : null,
        StyleIDs: buildTiered(formData.StyleIDs, Number),
        LabelIDs: buildTiered(formData.LabelIDs, String),
        MethodIDs: buildTiered(formData.MethodIDs, Number),
        ...(() => {
          // Mutually exclusive: custom mode persists only the typed code (catalog cleared);
          // catalog mode persists the picked refs (manual cleared).
          if (formData.upcMode === 'custom') {
            const code = digitsOnly(formData.manualUpc);
            return { UPCRefs: [], UPCIDs: [], PIDBagIDs: [], ManualUPCs: code ? [code] : [] };
          }
          const u = upcSelectionsToPayload(formData.upcSelectionKeys || []);
          return { UPCRefs: u.UPCRefs, UPCIDs: u.UPCIDs, PIDBagIDs: u.PIDBagIDs, ManualUPCs: [] };
        })(),
        ActorName: user?.name || 'Unknown',
      };
      const res = await authAxios.post('/api/customer-specs', payload);
      if (res.status >= 200 && res.status < 300) {
        savedToast('Customer Specification');
        initialSnapshotRef.current = initialForm;
        setIsDirty(false);
        navigate('/CustomerSpecs/Manage');
      }
    } catch (err) {
      console.error('[CustomerSpecs] Submit failed', err);
      const apiFields = err?.response?.data?.fields;
      const missingErrs = {};
      if (Array.isArray(apiFields)) apiFields.forEach(f => { missingErrs[f] = true; });
      setFieldErrors(missingErrs);
      if (err?.response?.status === 409) {
        errorToast('A similar specification already exists.');
      } else if (err?.response?.status === 400) {
        errorToast('Please complete all required fields.');
      } else {
        errorToast();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reference data + derived option memos passed to the shared field body.
  const refs = {
    customers, commodities, styles, labels, methods, methodOptions, tihi, caseTags,
    pluMap, pluImagesByLabelId,
    destinationOptions, sizeOptionsForCommodity, varietyOptionsForCommodity,
    gradeOptionsForCommodity, derivedPLUIds, mergedUpcOptions, upcOptionByValue, tihiById,
    bagLabels: bagLabelRows,
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormShell>
      <PageHeader>
        <div>Customer Specifications | Entry Form</div>
        <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
          <StatusPicker
            status={formData.Status}
            size="sm"
            ariaLabel="Specification status"
            onChange={(s) => setFormData(prev => ({ ...prev, Status: s }))}
          />
          <div aria-label="Universal toggle" style={{
            display:'inline-flex',
            background:'var(--white-a20)',
            borderRadius: 10,
            padding: 4,
            boxShadow: 'inset 0 1px 2px var(--shadow-a15)',
          }}>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, Universal: false, Destinations: (prev.Destinations || []).includes('__ALL__') ? [] : (prev.Destinations || []) }))}
              style={{
                border:'none',
                cursor:'pointer',
                padding:'6px 14px',
                borderRadius:8,
                background: formData.Universal ? 'transparent' : 'var(--white)',
                color: formData.Universal ? 'var(--cream-400)' : 'var(--color-text)',
                boxShadow: formData.Universal ? 'none' : '0 2px 6px var(--shadow-a25)',
                transition:'all 120ms ease',
              }}
            >
              DC Specific
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, Universal: true, Destinations: ['__ALL__'] }))}
              style={{
                border:'none',
                cursor:'pointer',
                padding:'6px 14px',
                borderRadius:8,
                background: formData.Universal ? 'var(--white)' : 'transparent',
                color: formData.Universal ? 'var(--color-text)' : 'var(--cream-400)',
                boxShadow: formData.Universal ? '0 2px 6px var(--shadow-a25)' : 'none',
                transition:'all 120ms ease',
              }}
            >
              All Destinations
            </button>
          </div>
        </div>
      </PageHeader>

      <SpecFormFields
        mode="create"
        form={formData}
        setForm={setFormData}
        refs={refs}
        fieldErrors={fieldErrors}
        onLightbox={setLightboxSrc}
      />

      <div style={{ position: 'sticky', bottom: 0, zIndex: 10, display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', marginTop: 8, padding: '12px 4px', background: 'var(--cream-50)', borderTop: '1px solid var(--shadow-a08)' }}>
        <Button type="button" onClick={() => navigate('/CustomerSpecs/Manage')} style={{ background: 'var(--grey-500)' }}>Cancel</Button>
        <Button type="submit" disabled={isSubmitting} style={{ padding: '8px 20px', fontSize: '0.9rem' }}>{isSubmitting ? 'Saving…' : 'Save'}</Button>
      </div>
      {lightboxSrc && (
        <LightboxBackdrop onClick={() => setLightboxSrc(null)}>
          <LightboxImage src={lightboxSrc} alt="Preview" />
        </LightboxBackdrop>
      )}
      </FormShell>
    </form>
  );
};

export default CustomerSpecsFormPage;
